import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, X } from "lucide-react";
import DropZone from "../components/DropZone";
import TranslationTabs from "../components/TranslationTabs";

const HIDDEN_KEYS = new Set(["id", "_id", "is_deleted", "created_at", "updated_at", "order", "editor", "deleted_at"]);

function resolveSrc(u) { if (!u) return ""; if (u.startsWith("http")) return u; if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`; return u; }

function summarize(it) {
  return Object.entries(it)
    .filter(([k, v]) => !HIDDEN_KEYS.has(k) && v && typeof v !== "object")
    .slice(0, 3)
    .map(([k, v]) => `${k}: ${String(v).slice(0, 40)}`)
    .join(" · ");
}

const SCHEMAS = {
  services: { title: "Serviços", fields: [
    { k: "title", label: "Título" }, { k: "slug", label: "Slug" }, { k: "benefit", label: "Benefício/slogan" },
    { k: "pillar", label: "Pilar" }, { k: "desc", label: "Descrição", area: true },
    { k: "departments", label: "Departamentos (vírgulas)", arr: true },
    { k: "options", label: "Opções de Prazo (vírgulas) — ex: 6 meses, 12 meses, 24 meses", arr: true },
    { k: "remuneration", label: "Opções de Remuneração (vírgulas) — ex: Preço Fixo, Fixo + variável, Variável", arr: true },
    { k: "ideal_for", label: "Ideal Para — texto curto (ex: Incorporadoras médias e grandes que querem escalar lançamentos)", area: true },
    { k: "ideal_for_label", label: "Rótulo do 'Ideal Para' (padrão: 'Ideal para quem:')", area: false },
  ]},
  team: { title: "Equipe", fields: [
    { k: "name", label: "Nome" }, { k: "role", label: "Cargo" },
    { k: "bio", label: "Bio", area: true }, { k: "photo", label: "Foto", img: true },
    { k: "linkedin", label: "LinkedIn" },
  ]},
  founders: { title: "Fundadores", fields: [
    { k: "name", label: "Nome" }, { k: "role", label: "Cargo" },
    { k: "bio", label: "Bio", area: true }, { k: "photo", label: "Foto", img: true },
    { k: "linkedin", label: "LinkedIn" },
  ]},
  clients: { title: "Clientes", fields: [
    { k: "name", label: "Nome" }, { k: "category", label: "Categoria" },
    { k: "logo", label: "Logo (PNG/SVG transparente — altura fixa 40px na home)", img: true },
  ]},
  history: { title: "Histórico de projetos", fields: [
    { k: "year", label: "Ano", num: true }, { k: "developer", label: "Empresa (Incorporadora)" },
    { k: "developer_url", label: "Site da Empresa (link no nome)" },
    { k: "project", label: "Empreendimento" }, { k: "city", label: "Cidade/UF" },
    { k: "type", label: "Tipo — LEGADO (use 'types' abaixo para múltiplos)" },
    { k: "types", label: "Tipos de projeto (múltiplos, ex: LANÇAMENTO, SUSTENTAÇÃO, RELANÇAMENTO)", arr: true },
  ]},
  jobs: { title: "Vagas Abertas", translatable_context: "job", fields: [
    { k: "title", label: "Título da vaga", i18n: true },
    { k: "location", label: "Local (ex: Goiânia/GO · Remoto · Híbrido)", i18n: true },
    { k: "department", label: "Departamento", i18n: true },
    { k: "employment_type", label: "Regime (CLT · PJ · Estágio)", i18n: true },
    { k: "is_open", label: "Vaga aberta (aparece no site público)", bool: true },
    { k: "overview", label: "Visão geral do cargo", area: true, i18n: true },
    { k: "responsibilities", label: "Responsabilidades (uma por linha, separada por vírgula)", arr: true, i18n: true },
    { k: "requirements", label: "Requisitos (uma por linha, separada por vírgula)", arr: true, i18n: true },
    { k: "extras", label: "Informações adicionais (separadas por vírgula)", arr: true, i18n: true },
  ]},
  careers_videos: { title: "Vídeos: Como é trabalhar aqui", fields: [
    { k: "title", label: "Título do card" },
    { k: "subtitle", label: "Subtítulo (opcional)" },
    { k: "url", label: "URL do vídeo (YouTube, Vimeo ou MP4 direto)" },
  ]},
};

export default function CollectionAdmin() {
  const { coll } = useParams();
  const schema = SCHEMAS[coll];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  // Admin needs to see ALL items (including closed jobs and hidden entries),
  // NOT the public-filtered list. `/admin/collection/{coll}` returns everything
  // except soft-deleted rows.
  const load = async () => { const { data } = await api.get(`/admin/collection/${coll}`); setItems(data); };
  useEffect(() => { load(); }, [coll]);

  const startNew = () => setEditing(Object.fromEntries(schema.fields.map(f => [f.k, f.arr ? [] : (f.bool ? true : (f.num ? 0 : ""))])));
  const save = async () => {
    const data = { ...editing };
    schema.fields.forEach(f => {
      if (f.arr && typeof data[f.k] === "string") data[f.k] = data[f.k].split(",").map(s => s.trim()).filter(Boolean);
      if (f.num) data[f.k] = Number(data[f.k]);
      // Normalise legacy string values ("true"/"false") coming from previously saved records.
      if (f.bool) data[f.k] = data[f.k] === true || data[f.k] === "true";
    });
    if (data.id) await api.put(`/admin/${coll}/${data.id}`, data);
    else await api.post(`/admin/${coll}`, data);
    toast.success("Salvo.");
    setEditing(null);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Excluir?")) return;
    await api.delete(`/admin/${coll}/${id}`);
    load();
  };

  const rows = useMemo(() => items.map((it) => ({ ...it, _summary: summarize(it) })), [items]);

  if (!schema) return <div>Coleção desconhecida.</div>;

  return (
    <div>
      <div className="flex justify-between items-end mb-6">
        <h1 className="font-display text-4xl">{schema.title}</h1>
        <button onClick={startNew} className="bg-accent-yellow text-black text-sm font-semibold px-4 py-2 rounded-full" data-testid="coll-new"><Plus className="w-4 h-4 inline mr-1" /> Novo</button>
      </div>

      <div className="space-y-2" data-testid="coll-list">
        {rows.map((it) => (
          <div key={it.id} className="flex items-center gap-3 border border-black/10 p-3 rounded-md hover:bg-black/[0.02]">
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">
                {it.title || it.name || it.project || `Item ${it.id.slice(0,6)}`}
                {coll === "jobs" && !(it.is_open === true || it.is_open === "true") && (
                  <span className="ml-2 text-[10px] uppercase tracking-widest bg-neutral-200 text-neutral-600 px-2 py-0.5 rounded">· Fechada</span>
                )}
              </div>
              <div className="text-xs text-neutral-500 truncate">{it._summary}</div>
            </div>
            <button onClick={() => setEditing(it)} className="text-xs border border-black/15 px-3 py-1.5 rounded" data-testid={`coll-edit-${it.id?.slice(0,6)}`}>Editar</button>
            <button onClick={() => remove(it.id)} className="text-xs text-red-500 px-3 py-1.5"><Trash2 className="w-3 h-3 inline" /></button>
          </div>
        ))}
        {!rows.length && <div className="text-sm text-neutral-500 text-center py-12">Vazio.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-20">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEditing(null)} />
          <div className="relative bg-white max-w-2xl w-full rounded-lg p-6 space-y-3 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center"><div className="font-display text-xl">{editing.id ? "Editar" : "Novo"}</div><button onClick={() => setEditing(null)}><X className="w-4 h-4" /></button></div>
            {schema.fields.map(f => (
              <div key={f.k}>
                <label className="text-xs text-neutral-500">{f.label}</label>
                {f.area ? (
                  <textarea value={editing[f.k] ?? ""} onChange={(e) => setEditing({...editing, [f.k]: e.target.value})} rows={3} className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
                ) : f.bool ? (
                  <label className="flex items-center gap-2 border border-black/15 rounded px-3 py-2 cursor-pointer">
                    <input type="checkbox"
                      checked={editing[f.k] === true || editing[f.k] === "true"}
                      onChange={(e) => setEditing({ ...editing, [f.k]: e.target.checked })}
                      data-testid={`coll-field-${f.k}`}
                    />
                    <span className="text-sm text-neutral-700">{editing[f.k] === true || editing[f.k] === "true" ? "Sim" : "Não"}</span>
                  </label>
                ) : f.img ? (
                  <div>
                    {editing[f.k] && <img src={resolveSrc(editing[f.k])} alt="" className="max-h-16 mb-2 bg-neutral-100 p-2 rounded" />}
                    <DropZone multiple={false} onUploaded={(u) => setEditing({...editing, [f.k]: u.url})} />
                    <input type="text" value={editing[f.k] ?? ""} onChange={(e) => setEditing({...editing, [f.k]: e.target.value})} placeholder="ou URL" data-testid={`coll-field-${f.k}`} className="w-full text-xs border border-black/15 px-2 py-1.5 rounded mt-2" />
                  </div>
                ) : (
                  <input type={f.num ? "number" : "text"} value={f.arr ? (Array.isArray(editing[f.k]) ? editing[f.k].join(", ") : editing[f.k]) : (editing[f.k] ?? "")} onChange={(e) => setEditing({...editing, [f.k]: e.target.value})} data-testid={`coll-field-${f.k}`} className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
                )}
              </div>
            ))}
            {schema.translatable_context && (
              <TranslationTabs
                value={editing.translations || {}}
                onChange={(v) => setEditing({ ...editing, translations: v })}
                sourceFields={Object.fromEntries(
                  schema.fields.filter((f) => f.i18n).map((f) => [f.k, editing[f.k]])
                )}
                fields={schema.fields.filter((f) => f.i18n).map((f) => ({
                  key: f.k, label: f.label, area: !!f.area, arr: !!f.arr,
                }))}
                context={schema.translatable_context}
                testidPrefix={`${coll}-tt`}
              />
            )}
            <button onClick={save} data-testid="coll-save" className="bg-black text-white text-sm px-5 py-2.5 rounded-full"><Save className="w-3 h-3 inline mr-1" /> Salvar</button>
          </div>
        </div>
      )}
    </div>
  );
}
